import { obtenerNombreEmpresaReal } from '../../../config/unificacion';
import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { useCorreosMarketingModel } from '../../../modelos/EmailMarkeeting/modeloCorreos';
import React, { useEffect } from 'react';

const CampañasCorreos = () => {
  const {
    obtenerCampanas,
    campanas,
    loadingCampanas,
    errorCampanas,
  } = useCorreosMarketingModel();
  
  const { configuracion } = useConfiguracion();
  const nombreEmpresa = obtenerNombreEmpresaReal(configuracion.nombreEmpresa);

  useEffect(() => {
    if (nombreEmpresa) {
      obtenerCampanas(nombreEmpresa);
    }
  }, [nombreEmpresa]);

  // Helper para renderizar los badges de estado estéticos
  const obtenerBadgeEstado = (estado) => {
    const estilos = {
      completada: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      enviando: 'bg-blue-50 text-blue-700 border-blue-200',
      fallido: 'bg-red-50 text-red-700 border-red-200',
    };
    
    const claseActual = estilos[estado] || 'bg-gray-50 text-gray-700 border-gray-200';
    
    return (
      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider ${claseActual}`}>
        {estado}
      </span>
    );
  };

  // Helper para formatear las fechas de Postgres de manera amigable
  const formatearFecha = (fechaString) => {
    if (!fechaString) return '---';
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper para desempaquetar y mostrar los filtros aplicados en la campaña
  const renderFiltros = (filtrosRaw) => {
    try {
      const filtros = typeof filtrosRaw === 'string' ? JSON.parse(filtrosRaw) : filtrosRaw;
      if (!filtros) return <span className="text-gray-400 text-xs">Sin filtros</span>;

      return (
        <div className="flex flex-wrap gap-1">
          <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[10px] font-medium border">
            👥 {filtros.tipo || 'todos'}
          </span>
          {filtros.universidad && (
            <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[10px] font-medium border border-purple-100 truncate max-w-[110px]" title={filtros.universidad}>
              🏛️ {filtros.universidad}
            </span>
          )}
          {filtros.carrera && (
            <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-[10px] font-medium border border-indigo-100 truncate max-w-[110px]" title={filtros.carrera}>
              📚 {filtros.carrera}
            </span>
          )}
        </div>
      );
    } catch (e) {
      return <span className="text-gray-400 text-xs">Filtros personalizados</span>;
    }
  };

  return (
    <div className="space-y-5 animate-fadeIn text-left text-sm">
      
      {/* HEADER DE LA SECCIÓN */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm gap-2">
        <div>
          <h2 className="text-base font-bold text-gray-900">Historial de Campañas Masivas</h2>
          <p className="text-xs text-gray-400 mt-0.5">Monitoreo de envíos de email marketing y efectividad de la segmentación.</p>
        </div>
        <div className="bg-blue-50 text-blue-900 px-3 py-1.5 rounded-lg border border-blue-100 text-center font-bold">
          <span className="text-lg font-black block leading-none">{campanas?.length || 0}</span>
          <span className="text-[10px] uppercase text-blue-700 tracking-wider font-semibold">Creadas</span>
        </div>
      </div>

      {/* MANEJO DE ESTADOS DE LA PETICIÓN */}
      {loadingCampanas ? (
        <div className="bg-white rounded-xl border p-12 text-center shadow-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
          <p className="text-xs text-gray-400 mt-3 font-medium">Consultando el historial en PostgreSQL...</p>
        </div>
      ) : errorCampanas ? (
        <div className="bg-red-50 text-red-800 p-4 rounded-xl border border-red-200 flex items-center space-x-2">
          <span className="text-lg">⚠️</span>
          <p className="text-xs font-semibold">Error al cargar las campañas: {errorCampanas}</p>
        </div>
      ) : campanas.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center shadow-sm">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0a2 2 0 01-2 2H6a2 2 0 01-2-2m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-4m-8 0H4"></path>
          </svg>
          <h4 className="text-xs font-bold text-gray-700 uppercase">No hay campañas registradas</h4>
          <p className="text-xs text-gray-400 mt-0.5">Ve al módulo de "Nueva Campaña" para realizar tu primer despacho masivo.</p>
        </div>
      ) : (
        
        /* TABLA DE HISTORIAL TOTALMENTE RESPONSIVE CON MÉTRICAS */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-100 text-gray-500 text-[11px] font-bold uppercase tracking-wider">
                  <th className="py-3 px-4">Campaña / Diseño</th>
                  <th className="py-3 px-4">Segmentación</th>
                  <th className="py-3 px-4">Rendimiento Envíos</th>
                  <th className="py-3 px-4 text-center">Tasa Apertura</th>
                  <th className="py-3 px-4 text-center">Estado</th>
                  <th className="py-3 px-4 text-right">Fecha de Envío</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                {campanas.map((campana) => {
                  // Cálculos analíticos por fila basados en agregaciones de Postgres
                  const entregados = campana.total_destinatarios - (campana.total_fallidos || 0);
                  const openRate = entregados > 0 
                    ? Math.round(((campana.total_abiertos || 0) / entregados) * 100) 
                    : 0;

                  // Estilos dinámicos para los rangos de conversión
                  const colorPorcentaje = openRate > 60 
                    ? 'text-emerald-600 font-bold' 
                    : openRate > 30 ? 'text-blue-600 font-bold' : 'text-amber-600 font-medium';

                  return (
                    <tr key={campana.id} className="hover:bg-slate-50/60 transition-colors">
                      
                      {/* NOMBRE Y PLANTILLA */}
                      <td className="py-3.5 px-4 max-w-[200px]">
                        <span className="font-bold text-gray-900 block truncate" title={campana.nombre}>
                          {campana.nombre}
                        </span>
                        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded inline-block mt-1">
                          🎨 {campana.plantilla_nombre || 'Sin plantilla interna'}
                        </span>
                      </td>
                      
                      {/* FILTROS APLICADOS */}
                      <td className="py-3.5 px-4">
                        {renderFiltros(campana.filtros_aplicados)}
                      </td>
                      
                      {/* DESGLOSE RENDIMIENTO DE ENVÍOS */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5 text-[11px]">
                          <div className="flex justify-between space-x-4">
                            <span className="text-gray-400">Objetivos:</span>
                            <span className="font-semibold text-gray-700">{campana.total_destinatarios}</span>
                          </div>
                          {campana.total_fallidos > 0 && (
                            <div className="flex justify-between text-red-500 font-medium">
                              <span>Rebotes:</span>
                              <span>{campana.total_fallidos}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* TASA DE APERTURA REAL CON PÍXEL TRACKING */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="inline-block text-center">
                          <span className={`text-sm block leading-none ${colorPorcentaje}`}>
                            {openRate}%
                          </span>
                          <span className="text-[10px] text-gray-400 font-mono mt-0.5 block">
                            ({campana.total_abiertos || 0} de {entregados} abiertos)
                          </span>
                          
                          {/* Minibarra de progreso visual */}
                          <div className="w-20 bg-gray-100 h-1 rounded-full mx-auto mt-1 overflow-hidden border">
                            <div 
                              style={{ width: `${openRate}%` }} 
                              className={`h-full transition-all duration-500 ${
                                openRate > 60 ? 'bg-emerald-500' : openRate > 30 ? 'bg-blue-500' : 'bg-amber-500'
                              }`}
                            />
                          </div>
                        </div>
                      </td>
                      
                      {/* STATUS BADGE */}
                      <td className="py-3.5 px-4 text-center">
                        {obtenerBadgeEstado(campana.estado)}
                      </td>
                      
                      {/* FECHA ENVIO */}
                      <td className="py-3.5 px-4 text-right text-gray-500 font-mono text-[11px]">
                        {formatearFecha(campana.fecha_envio || campana.created_at)}
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default CampañasCorreos;